import * as React from 'react';

import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import RepairNamesDialog, {
  RepairChoices,
} from 'components/ReportRequestV2/RepairNamesDialog';

import styles from './PasteProductsDialog.module.css';
import type { CatalogProduct } from '../ProductPicker';

const normalise = (name: string) => name.trim().toLowerCase();

const plural = (n: number, word: string) =>
  `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;

const DEFAULT_DESCRIPTION =
  "One product per line, e.g. copied from a spreadsheet column. Names must match the catalog exactly; case only matters when two products differ by it. Repair any that don't: the repaired name replaces what you pasted and is highlighted. Every workspace that has a product is added.";

/** Rows and tab-separated cells, kept in the split so the text can be rebuilt. */
const CELL_SEPARATOR = /(\n|\t)/;

/** A cell's other line breaks, e.g. from a wrapped spreadsheet cell, become spaces. */
const cleanCell = (cell: string) =>
  cell.replace(/[\r\v\f\u2028\u2029]+/g, ' ').trim();

/** A repair written into the pasted text, at its position in `text.split(CELL_SEPARATOR)`. */
export interface CellRepair {
  index: number;
  pasted: string;
  name: string;
}

/** Replaces each cell `fixes` maps (by its cleaned text) with the mapped name, recording where. */
export const repairCells = (
  text: string,
  fixes: ReadonlyMap<string, string>,
): { text: string; repairs: CellRepair[] } => {
  const cells = text.split(CELL_SEPARATOR);
  const repairs: CellRepair[] = [];
  cells.forEach((cell, index) => {
    const pasted = cleanCell(cell);
    const name = fixes.get(pasted);
    if (name) {
      cells[index] = name;
      repairs.push({ index, pasted, name });
    }
  });
  return { text: cells.join(''), repairs };
};

/** The repairs whose cell still holds the repaired name, i.e. not since edited away. */
const liveRepairs = (text: string, repairs: CellRepair[]) => {
  const cells = text.split(CELL_SEPARATOR);
  return repairs.filter((r) => cleanCell(cells[r.index] ?? '') === r.name);
};

export interface PasteMatch {
  matched: CatalogProduct[];
  matchedNames: string[];
  unmatched: string[];
}

/**
 * Resolves pasted product names (one per line, or tab separated) against the
 * catalog. The exact name wins; otherwise case is ignored, unless several
 * catalog names differ only by case (e.g. `Nutella` and `NUTELLA`), which are
 * different products: those are left unmatched for a person to choose.
 */
export const matchPastedNames = (
  text: string,
  catalog: CatalogProduct[],
): PasteMatch => {
  const names = Array.from(
    new Set(text.split(CELL_SEPARATOR).map(cleanCell).filter(Boolean)),
  );
  const rowsByName = new Map<string, CatalogProduct[]>();
  const namesByKey = new Map<string, Set<string>>();
  catalog.forEach((p) => {
    rowsByName.set(p.name, [...(rowsByName.get(p.name) ?? []), p]);
    const key = normalise(p.name);
    namesByKey.set(key, (namesByKey.get(key) ?? new Set()).add(p.name));
  });
  const resolve = (pasted: string) => {
    const exact = Array.from(namesByKey.get(normalise(pasted)) ?? []).filter(
      (n) => n.trim() === pasted,
    );
    const candidates = exact.length
      ? exact
      : Array.from(namesByKey.get(normalise(pasted)) ?? []);
    return candidates.length === 1 ? candidates[0] : undefined;
  };
  const matched: CatalogProduct[] = [];
  const matchedNames: string[] = [];
  const unmatched: string[] = [];
  names.forEach((pasted) => {
    const name = resolve(pasted);
    // Different spellings can resolve to one product, e.g. `nutella` and `Nutella`.
    if (name && matchedNames.includes(name)) return;
    if (name) {
      matched.push(...(rowsByName.get(name) ?? []));
      matchedNames.push(name);
    } else {
      unmatched.push(pasted);
    }
  });
  return { matched, matchedNames, unmatched };
};

export interface PasteProductsDialogProps {
  open: boolean;
  onClose: () => void;
  catalog: CatalogProduct[];
  onAdd: (products: CatalogProduct[]) => void;
  description?: string;
  /** Names already in the set; they're reported but not counted or added again. */
  addedNames?: ReadonlySet<string>;
  /** Hands the names that weren't found to the fuzzy finder. */
  onFuzzyFind?: (names: string[]) => void;
}

/**
 * Greys out repaired names in the text above, drawn over the textarea (which
 * can't style its own text) with transparent text in the same font and wrap.
 */
const RepairHighlights: React.FC<{
  text: string;
  /** Cell positions to highlight, as in `CellRepair.index`. */
  cells: ReadonlySet<number>;
  input: React.RefObject<HTMLTextAreaElement>;
  overlay: React.RefObject<HTMLDivElement>;
}> = ({ text, cells, input, overlay }) => {
  const [box, setBox] = React.useState<React.CSSProperties>({});
  React.useLayoutEffect(() => {
    const el = input.current;
    if (!el) return;
    const next = {
      top: el.offsetTop,
      left: el.offsetLeft,
      width: el.clientWidth,
      height: el.clientHeight,
    };
    setBox((prev) =>
      (Object.keys(next) as (keyof typeof next)[]).every(
        (k) => prev[k] === next[k],
      )
        ? prev
        : next,
    );
    if (overlay.current) overlay.current.scrollTop = el.scrollTop;
  });
  return (
    <div ref={overlay} className={styles.highlights} style={box} aria-hidden>
      {text.split(CELL_SEPARATOR).map((cell, i) =>
        cells.has(i) ? (
          <mark key={i} data-testid="repaired-highlight">
            {cell}
          </mark>
        ) : (
          cell
        ),
      )}
    </div>
  );
};

/** Bulk-add products by pasting their exact names. */
const PasteProductsDialog: React.FC<PasteProductsDialogProps> = ({
  open,
  onClose,
  catalog,
  onAdd,
  description = DEFAULT_DESCRIPTION,
  addedNames,
  onFuzzyFind,
}) => {
  const [text, setText] = React.useState('');
  const [copied, setCopied] = React.useState(false);
  // Repair decisions not yet applied to the text: skips, and a repair being changed.
  const [choices, setChoices] = React.useState<RepairChoices>({});
  // Cells rewritten by a repair, so only those are highlighted, listed and undone.
  const [repairs, setRepairs] = React.useState<CellRepair[]>([]);
  // The name the repair dialog opens on; `undefined` while it's closed.
  const [repairAt, setRepairAt] = React.useState<string | null | undefined>(
    undefined,
  );
  const inputRef = React.useRef<HTMLTextAreaElement>(null);
  const highlightRef = React.useRef<HTMLDivElement>(null);
  const result = React.useMemo(
    () => (text.trim() ? matchPastedNames(text, catalog) : null),
    [text, catalog],
  );
  const catalogNames = React.useMemo(
    () => new Set(catalog.map((p) => p.name)),
    [catalog],
  );

  const isNew = (name: string) => !addedNames?.has(name);
  const notFound = result?.unmatched ?? [];
  const matchedNames = result?.matchedNames ?? [];
  const repaired = liveRepairs(text, repairs);
  const repairedCells = new Set(repaired.map((r) => r.index));
  const repairedCount = new Set(repaired.map((r) => r.name)).size;
  const newNames = matchedNames.filter(isNew);
  const alreadyAdded = matchedNames.length - newNames.length;
  const toAdd = () =>
    (result?.matched ?? []).filter(
      (p, i, all) => isNew(p.name) && all.findIndex((q) => q.id === p.id) === i,
    );

  /** Writes confirmed repairs into the text; skips stay as choices. */
  const applyRepairs = (decisions: RepairChoices) => {
    const fixes = new Map(
      Object.entries(decisions).filter(
        (d): d is [string, string] => !!d[1] && catalogNames.has(d[1]),
      ),
    );
    const next = repairCells(text, fixes);
    setText(next.text);
    setRepairs((prev) => [...liveRepairs(next.text, prev), ...next.repairs]);
    setChoices(
      Object.fromEntries(
        Object.entries(decisions).filter(([, name]) => name === null),
      ),
    );
    setRepairAt(undefined);
  };

  const undo = (r: CellRepair) => {
    const cells = text.split(CELL_SEPARATOR);
    cells[r.index] = r.pasted;
    setText(cells.join(''));
    setRepairs((prev) => prev.filter((x) => x !== r));
  };

  const change = (r: CellRepair) => {
    undo(r);
    setChoices((c) => ({ ...c, [r.pasted]: r.name }));
    setRepairAt(r.pasted);
  };

  const close = () => {
    setText('');
    setCopied(false);
    setChoices({});
    setRepairs([]);
    setRepairAt(undefined);
    onClose();
  };

  const add = () => {
    onAdd(toAdd());
    close();
  };

  const copyNotFound = async () => {
    try {
      await navigator.clipboard.writeText(notFound.join('\n'));
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="md">
      <DialogTitle>Paste product names</DialogTitle>
      <DialogContent>
        <Box className={styles.body}>
          <DialogContentText>{description}</DialogContentText>
          <TextField
            autoFocus
            multiline
            minRows={6}
            maxRows={14}
            fullWidth
            label="Product names"
            value={text}
            inputRef={inputRef}
            onChange={(e) => {
              setText(e.target.value);
              setCopied(false);
            }}
            onKeyDown={(e) => {
              if (
                e.key === 'Enter' &&
                (e.metaKey || e.ctrlKey) &&
                newNames.length > 0
              ) {
                e.preventDefault();
                add();
              }
            }}
            InputProps={{
              startAdornment:
                repaired.length > 0 ? (
                  <RepairHighlights
                    text={text}
                    cells={repairedCells}
                    input={inputRef}
                    overlay={highlightRef}
                  />
                ) : undefined,
            }}
            inputProps={{
              onScroll: (e: React.UIEvent<HTMLTextAreaElement>) => {
                if (highlightRef.current)
                  highlightRef.current.scrollTop = e.currentTarget.scrollTop;
              },
            }}
            helperText="Press ⌘/Ctrl + Enter to add."
          />
          {result && (
            <Typography variant="body2" aria-live="polite">
              {newNames.length} matched
              {repairedCount > 0 && ` (${repairedCount} repaired)`}
              {alreadyAdded > 0 && `, ${alreadyAdded} already added`}
              {notFound.length > 0 && `, ${notFound.length} not found`}
            </Typography>
          )}
          {repaired.length > 0 && (
            <Box role="group" aria-label="Repaired names">
              <Typography variant="subtitle2" gutterBottom>
                Repaired, highlighted above
              </Typography>
              <Box component="ul" className={styles.repairList}>
                {repaired.map((r) => (
                  <li key={r.index} className={styles.repairRow}>
                    <span className={styles.from}>{r.pasted}</span>
                    <span aria-hidden>→</span>
                    <strong className={styles.to}>{r.name}</strong>
                    <Box className={styles.rowActions}>
                      <Button
                        size="small"
                        onClick={() => change(r)}
                        aria-label={`Change the repair of ${r.pasted}`}
                      >
                        Change
                      </Button>
                      <Button
                        size="small"
                        color="inherit"
                        onClick={() => undo(r)}
                        aria-label={`Undo the repair of ${r.pasted}`}
                      >
                        Undo
                      </Button>
                    </Box>
                  </li>
                ))}
              </Box>
            </Box>
          )}
          {notFound.length > 0 && (
            <Alert severity="warning" className={styles.notFoundAlert}>
              <Typography variant="body2" gutterBottom>
                Not found in the product catalog:
              </Typography>
              <Box className={`${styles.chips} ${styles.notFound}`}>
                {notFound.map((name) => (
                  <Chip
                    key={name}
                    size="small"
                    label={choices[name] === null ? `${name} · skipped` : name}
                    onClick={() => setRepairAt(name)}
                    title="Repair this name"
                  />
                ))}
              </Box>
              <Box className={styles.alertActions}>
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  startIcon={<AutoFixHighIcon />}
                  onClick={() => setRepairAt(null)}
                  disabled={catalog.length === 0}
                >
                  Repair {notFound.length}
                </Button>
                <Button color="inherit" size="small" onClick={copyNotFound}>
                  {copied ? 'Copied' : 'Copy'}
                </Button>
                {onFuzzyFind && (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      // Add the exact matches first so nothing pasted is lost on the way.
                      if (newNames.length > 0) onAdd(toAdd());
                      onFuzzyFind(notFound);
                      close();
                    }}
                  >
                    Fuzzy find {notFound.length}
                  </Button>
                )}
              </Box>
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions className={styles.actions}>
        <Button onClick={close}>Cancel</Button>
        <Button
          variant="contained"
          disabled={newNames.length === 0}
          onClick={add}
        >
          Add {plural(newNames.length, 'product')}
        </Button>
      </DialogActions>
      {/* Mounted only while open: it indexes the whole catalog and loads photos. */}
      {repairAt !== undefined && (
        <RepairNamesDialog
          open
          names={notFound}
          catalog={catalog}
          initialChoices={choices}
          startAt={repairAt ?? undefined}
          addedNames={addedNames}
          // Cancelling still keeps a repair that was being changed.
          onClose={() => applyRepairs(choices)}
          onDone={applyRepairs}
        />
      )}
    </Dialog>
  );
};

export default PasteProductsDialog;
